import { useEffect, useState, useCallback } from "react";
import { T } from "@/components/T";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import {
  premiumPlan, premiumStatus, premiumSubscribe, premiumVerify, premiumCancel, premiumHistory,
  premiumGetBirth, premiumSaveBirth, premiumDeleteBirth, premiumGetLocations, premiumSaveLocations,
  premiumGetConsultation, premiumBookConsultation,
} from "@/lib/api";
import PremiumPlaceInput from "@/components/PremiumPlaceInput";
import MyDay from "@/components/MyDay";
import PremiumTools from "@/components/PremiumTools";
import NotificationSettings from "@/components/NotificationSettings";
import { premiumBirthStatus } from "@/lib/api";
import { Check, Loader2, Crown, Clock, Trash2, Plus, Star, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FEATURES = [
  ["Daily Panchangam with Current Hora & sun-path", "वर्तमान होरा एवं सूर्य-पथ सहित दैनिक पंचांगम्"],
  ["Auspicious & inauspicious Muhurta windows", "शुभ एवं अशुभ मुहूर्त काल"],
  ["Favourable Horas, Tarabala & Chandrashtama alerts", "शुभ होरा, ताराबल एवं चन्द्राष्टम चेतावनी"],
  ["Birth chart (D1/D9) with Bhavas & planets", "जन्म कुंडली (D1/D9), भाव एवं ग्रह सहित"],
  ["Vimshottari Dasha timeline (Maha → Pratyantar)", "विम्शोत्तरी दशा समयरेखा (महा → प्रत्यंतर)"],
  ["108-bead Japa counter & saved locations", "108-मनका जप गणक एवं सुरक्षित स्थान"],
];

function ConsultationCard({ pick }) {
  const [b, setB] = useState(undefined);
  const [phone, setPhone] = useState("");
  const [pref, setPref] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { premiumGetConsultation().then((d) => setB(d?.booking || null)).catch(() => setB(null)); }, []);
  if (b === undefined) return null;
  if (b) return (
    <div className="mt-5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="consultation-booked">
      <p className="flex items-center gap-2 text-sm font-medium text-emerald-700"><Check className="h-4 w-4" /> {pick("परामर्श अनुरोधित", "Consultation requested")}</p>
      <p className="mt-1 text-xs text-ink-2">{pick("हमारे पुरोहित शीघ्र आपसे संपर्क करेंगे।", "Our purohit will reach out to you shortly to schedule your 15–20 min session.")}</p>
    </div>
  );
  const submit = async () => {
    if (!phone.trim()) { toast.error(pick("कृपया फ़ोन नंबर दें", "Please add a phone / WhatsApp number")); return; }
    setBusy(true);
    try { const r = await premiumBookConsultation({ phone, preferred: pref }); setB(r.booking); toast.success(pick("परामर्श अनुरोधित 🙏", "Consultation requested 🙏")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not book"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-5 rounded-xl border border-[var(--gold-muted)] bg-surface p-4" data-testid="consultation-form">
      <p className="font-serif-latin text-base text-ink">{pick("अपना निःशुल्क मुहूर्त परामर्श बुक करें", "Book your free Muhurta consultation")}</p>
      <p className="mt-1 text-xs text-ink-3">{pick("15–20 मिनट · फैमिली प्लान में सम्मिलित", "15–20 mins · included with your Family Plan")}</p>
      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="consultation-phone" placeholder={pick("फ़ोन / व्हाट्सऐप", "Phone / WhatsApp")} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
        <input value={pref} onChange={(e) => setPref(e.target.value)} data-testid="consultation-pref" placeholder={pick("पसंदीदा समय (वैकल्पिक)", "Preferred time (optional)")} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
      </div>
      <button onClick={submit} disabled={busy} data-testid="consultation-book" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />} {pick("परामर्श बुक करें", "Request consultation")}
      </button>
    </div>
  );
}

export default function PanchangaPremium() {
  const { user } = useAuth();
  const { pick } = useLang();
  const [plan, setPlan] = useState({ enabled: false, price: 99, currency: "INR", family_enabled: false, family_price: 499 });
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [birthExists, setBirthExists] = useState(false);

  const refresh = useCallback(() => {
    premiumPlan().then(setPlan).catch(() => {});
    if (user) {
      premiumStatus().then(setStatus).catch(() => {});
      premiumHistory().then(setHistory).catch(() => {});
    }
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);

  const isPremium = status?.active;

  useEffect(() => {
    if (isPremium) premiumBirthStatus().then((s) => setBirthExists(!!s.exists)).catch(() => {});
  }, [isPremium]);

  const subscribe = async (tier = "single") => {
    if (!user) { toast.error(pick("कृपया पहले लॉगिन करें", "Please log in first")); return; }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Checkout failed to load");
      const sub = await premiumSubscribe(tier);
      const rzp = new window.Razorpay({
        key: sub.key_id, subscription_id: sub.subscription_id,
        name: "Dharmartha", description: sub.tier === "family" ? pick("पंचांगम् प्रीमियम फैमिली", "Panchangam Premium Family") : pick("पंचांगम् प्रीमियम", "Panchangam Premium"),
        prefill: { name: sub.name, email: sub.email }, theme: { color: "#C75000" },
        handler: async (resp) => {
          try {
            await premiumVerify({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(pick("प्रीमियम सक्रिय!", "Premium activated! 🙏"));
            refresh();
          } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start checkout");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(pick("चक्र के अंत में सदस्यता रद्द करें?", "Cancel your subscription at the end of this cycle?"))) return;
    try { await premiumCancel(); toast.success(pick("चक्र-अंत पर रद्द होगी", "Will cancel at cycle end")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <>
      <Helmet><title>Panchangam Premium · Dharmartha</title></Helmet>
      <section className="relative overflow-hidden border-b border-line bg-[linear-gradient(135deg,rgba(199,80,0,0.10),rgba(176,141,87,0.06))]">
        <div className="mx-auto max-w-4xl px-5 py-14 md:px-8 md:py-16">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)] bg-surface/70 px-3 py-1 text-xs font-medium tracking-wide text-gold">
            <Crown className="h-3.5 w-3.5" /> <T>Premium</T>
          </span>
          <h1 className="mt-4 font-deva text-4xl text-ink deva-lh md:text-5xl">{pick("पंचांग प्रीमियम", "Panchangam Premium")}</h1>
          <p className="mt-3 max-w-2xl text-ink-2">{pick(
            "आपके जन्म-विवरण एवं सटीक स्थान के आधार पर वैयक्तिक पंचांग, मुहूर्त एवं ज्योतिष।",
            "A personalised Panchangam, muhurtas and jyotish — tuned to your birth details and exact location.")}</p>
          <div className="mt-6 flex items-baseline gap-2">
            <span className="font-serif-latin text-2xl text-ink-3 line-through decoration-saffron/70" data-testid="premium-price-original">₹199</span>
            <span className="font-serif-latin text-5xl text-ink" data-testid="premium-price">₹{plan.price}</span>
            <span className="text-ink-3">/ {pick("माह", "month")}</span>
            <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="premium-discount-badge">{pick("50% छूट", "50% OFF")}</span>
          </div>
          <Link to="/panchanga-premium/app" data-testid="open-dashboard-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0b0f1a] px-6 py-3 text-sm font-semibold text-amber-300 shadow-lg transition-transform hover:scale-[1.02]">
            <Crown className="h-4 w-4" /> {pick("डैशबोर्ड खोलें", "Open Panchangam Dashboard")}
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-12 md:px-8">
        <div className="grid gap-3 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex items-start gap-3 border border-line bg-surface p-4">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
              <span className="text-sm text-ink-2">{pick(f[1], f[0])}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-6" data-testid="premium-subscription">
          {!user ? (
            <div className="text-center">
              <p className="text-ink-2">{pick("प्रीमियम लेने हेतु लॉगिन करें।", "Log in to unlock Premium.")}</p>
              <Link to="/login" className="mt-3 inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">{pick("लॉगिन", "Log in")}</Link>
            </div>
          ) : isPremium ? (
            <div>
              <div className="flex items-center gap-2"><Crown className="h-5 w-5 text-saffron" /><span className="font-serif-latin text-xl text-ink">{pick("प्रीमियम सक्रिय", "Premium active")}</span></div>
              <p className="mt-2 text-sm text-ink-2" data-testid="premium-status-line">
                {pick("स्थिति", "Status")}: <b>{status.status}</b>
                {status.current_end && <> · {pick("अगला बिल", "Next billing")}: {String(status.current_end).slice(0, 10)}</>}
                {status.cancel_at_cycle_end && <> · <span className="text-saffron">{pick("चक्र-अंत पर रद्द", "cancels at cycle end")}</span></>}
              </p>
              {!status.cancel_at_cycle_end && (
                <button onClick={cancel} data-testid="premium-cancel" className="mt-4 border border-line px-4 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">{pick("सदस्यता रद्द करें", "Cancel subscription")}</button>
              )}
              {!status.family_plan && (
                <a href="#family" data-testid="upgrade-family-link" className="mt-4 block text-sm font-medium text-saffron link-underline">{pick("परिवार के सदस्य जोड़ने हेतु फैमिली प्लान में अपग्रेड करें →", "Upgrade to the Family Plan to unlock family members →")}</a>
              )}
            </div>
          ) : (
            <div className="text-center">
              {!plan.enabled && <p className="mb-3 text-sm text-ink-3">{pick("भुगतान शीघ्र सक्षम होगा।", "Payments are being enabled — check back soon.")}</p>}
              <button onClick={subscribe} disabled={busy || !plan.enabled} data-testid="premium-subscribe"
                className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                {pick(`प्रीमियम अनलॉक करें – ₹${plan.price}/माह`, `Unlock Panchangam Premium – ₹${plan.price}/month`)}
              </button>
              <p className="mt-2 text-xs text-ink-3">{pick("ऑटो-रिन्यू · कभी भी रद्द करें · UPI/कार्ड/नेटबैंकिंग", "Auto-renews · cancel anytime · UPI / card / netbanking")}</p>
              <a href="#family" className="mt-3 inline-block text-sm font-medium text-saffron link-underline">{pick("परिवार (5 सदस्य) हेतु फैमिली प्लान देखें →", "Need charts for the family? See the Family Plan (5 members) →")}</a>
            </div>
          )}
          {history.length > 0 && (
            <div className="mt-6 border-t border-line pt-4" data-testid="premium-history">
              <p className="mb-2 overline-label text-ink-3">{pick("भुगतान इतिहास", "Payment history")}</p>
              <ul className="space-y-1 text-sm text-ink-2">
                {history.map((h, i) => (
                  <li key={i} className="flex items-center justify-between"><span>{String(h.at).slice(0, 10)}</span><span>₹{h.amount} · {h.status}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Family Plan */}
        <div id="family" className="mt-8 scroll-mt-24 rounded-2xl border border-[var(--gold-muted)] bg-[linear-gradient(135deg,rgba(199,80,0,0.07),rgba(176,141,87,0.05))] p-6" data-testid="family-plan">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Crown className="h-5 w-5 text-saffron" /><h2 className="font-serif-latin text-xl text-ink">{pick("पंचांगम् प्रीमियम फैमिली प्लान", "Panchangam Premium — Family Plan")}</h2></div>
            <div className="flex items-baseline gap-1.5"><span className="font-serif-latin text-xl text-ink-3 line-through decoration-saffron/70" data-testid="family-price-original">₹799</span><span className="font-serif-latin text-3xl text-ink" data-testid="family-price">₹{plan.family_price || 499}</span><span className="text-ink-3">/ {pick("माह", "month")}</span><span className="ml-1 inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="family-discount-badge">{pick("छूट", "SAVE ₹300")}</span></div>
          </div>
          <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {[
              [pick("5 सदस्यों तक के जन्म-विवरण एवं कुंडली", "Birth details & charts for up to 5 members"),
               pick("प्रत्येक सदस्य हेतु वैयक्तिक पंचांगम्, दशा एवं ताराबल", "Personalised Panchangam, Dasha & Tarabala per member"),
               pick("1 निःशुल्क मुहूर्त परामर्श (15–20 मिनट)", "1 FREE Muhurta consultation (15–20 mins)"),
               pick("एकल प्रीमियम की सभी सुविधाएँ सम्मिलित", "Everything in single Premium included")],
            ][0].map((t, i) => (
              <div key={i} className="flex items-start gap-2.5 border border-line bg-surface p-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-saffron" /><span className="text-sm text-ink-2">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-ink-2">{pick(
            "परिवार के सदस्य जोड़ने की सुविधा केवल फैमिली प्लान पर उपलब्ध है।",
            "Adding family members is available only on the Family Plan.")}</p>
          {status?.family_plan ? (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-700" data-testid="family-plan-active"><Check className="h-4 w-4" /> {pick("फैमिली प्लान सक्रिय", "Family Plan active")}</p>
          ) : (
            <>
              {!plan.family_enabled && <p className="mt-3 text-xs text-ink-3">{pick("फैमिली प्लान बिलिंग शीघ्र सक्षम होगी।", "Family Plan payments are being enabled — check back soon.")}</p>}
              <button onClick={() => subscribe("family")} disabled={busy || !plan.family_enabled} data-testid="family-plan-subscribe"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />} {pick(`फैमिली प्लान लें – ₹${plan.family_price || 499}/माह`, `Get the Family Plan – ₹${plan.family_price || 499}/month`)}
              </button>
              <p className="mt-2 text-xs italic text-ink-3">{pick("भुगतान के बाद परामर्श निर्धारित होगा।", "The free consultation is scheduled after activation.")}</p>
            </>
          )}
          {status?.family_plan && <ConsultationCard pick={pick} />}
        </div>

        {!isPremium && <PremiumAboutWhy pick={pick} />}

        {isPremium && (
          <div className="mt-8 rounded-2xl border border-amber-400/30 bg-[linear-gradient(135deg,rgba(199,80,0,0.08),rgba(176,141,87,0.05))] p-6 text-center" data-testid="premium-open-dashboard">
            <Crown className="mx-auto h-8 w-8 text-saffron" />
            <h2 className="mt-3 font-serif-latin text-2xl text-ink">{pick("आपका पंचांगम् डैशबोर्ड तैयार है", "Your Panchangam dashboard is ready")}</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-ink-2">{pick(
              "दैनिक पंचांग, मुहूर्त, होरा, कुंडली, दशा एवं जप — सब एक ही स्थान पर। जन्म-विवरण डैशबोर्ड के भीतर जोड़ें या संपादित करें।",
              "Daily Panchangam, muhurtas, hora, kundali, dasha & japa — all in one place. Add or edit your birth details right inside the dashboard.")}</p>
            <Link to="/panchanga-premium/app" data-testid="open-dashboard-cta-2"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0b0f1a] px-6 py-3 text-sm font-semibold text-amber-300 shadow-lg transition-transform hover:scale-[1.02]">
              <Crown className="h-4 w-4" /> {pick("डैशबोर्ड खोलें", "Open Dashboard")}
            </Link>
          </div>
        )}

        <ComingSoonCard pick={pick} />
      </section>
    </>
  );
}

function BirthDetails({ pick, onChange }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", dob: "", tob: "", place: "", lat: null, lon: null, tz_offset_min: 330, tz_id: "", consent: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => { premiumGetBirth().then((d) => { if (d && d.dob) { setData(d); setForm((f) => ({ ...f, ...d })); } }).catch(() => {}); }, []);

  const save = async () => {
    if (!form.dob || !form.tob || form.lat == null) { toast.error(pick("तिथि, समय एवं स्थान भरें", "Fill date, time and place")); return; }
    if (!form.consent) { toast.error(pick("कृपया सहमति दें", "Please tick the consent box")); return; }
    setBusy(true);
    try { const d = await premiumSaveBirth(form); setData(d); onChange && onChange(true); toast.success(pick("जन्म-विवरण सहेजे गए", "Birth details saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(pick("जन्म-विवरण स्थायी रूप से हटाएँ?", "Permanently delete your birth details?"))) return;
    await premiumDeleteBirth(); setData(null); onChange && onChange(false);
    setForm({ name: "", dob: "", tob: "", place: "", lat: null, lon: null, tz_offset_min: 330, tz_id: "", consent: false });
    toast.success(pick("हटा दिया", "Deleted"));
  };

  const ch = data?.chart;
  const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
  return (
    <div className="mt-8 border border-line bg-surface p-6" data-testid="birth-details">
      <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><Star className="h-5 w-5 text-saffron" /> {pick("जन्म-विवरण", "Birth details")}</h2>
      <div className="mt-2 flex items-start gap-2 rounded-md border border-[var(--gold-muted)]/50 bg-[rgba(176,141,87,0.06)] p-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
        <p className="text-xs leading-relaxed text-ink-2">{pick(
          "आपका जन्म-विवरण संवेदनशील व्यक्तिगत डेटा है (DPDP अधिनियम, भारत)। यह एन्क्रिप्टेड रूप से केवल आपकी कुंडली गणना हेतु संग्रहीत होता है, किसी के साथ साझा नहीं किया जाता, और आप इसे कभी भी संपादित या स्थायी रूप से हटा सकते हैं।",
          "Your birth details are sensitive personal data under India's DPDP Act. They are stored solely to compute your chart, never shared with anyone, and you can edit or permanently delete them at any time.")}
          {data?.consent_at && <span className="mt-1 block text-ink-3">{pick("सहमति दी गई", "Consent given")}: {String(data.consent_at).slice(0, 10)}</span>}
        </p>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input className={inp} placeholder={pick("नाम", "Name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="birth-name" />
        <input className={inp} type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="birth-dob" />
        <input className={inp} type="time" value={form.tob} onChange={(e) => setForm({ ...form, tob: e.target.value })} data-testid="birth-tob" />
        <PremiumPlaceInput value={form.place} testid="birth-place" placeholder={pick("जन्म स्थान", "Birth place")}
          onSelect={(loc) => setForm({ ...form, place: loc.name, lat: loc.lat, lon: loc.lon, tz_offset_min: loc.tz_offset_min, tz_id: loc.tz_id })} />
      </div>
      <label className="mt-3 flex items-start gap-2.5 text-sm text-ink-2">
        <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="birth-consent" className="mt-0.5 h-4 w-4 accent-[var(--saffron)]" />
        {pick("मैं अपने जन्म-विवरण सुरक्षित रूप से संग्रहीत करने की सहमति देता/देती हूँ।", "I consent to Dharmartha securely storing my birth details to compute my chart.")}
      </label>
      <div className="mt-4 flex gap-3">
        <button onClick={save} disabled={busy} data-testid="birth-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {pick("सहेजें एवं गणना करें", "Save & compute")}</button>
        {data && <button onClick={remove} data-testid="birth-delete" className="inline-flex items-center gap-1.5 border border-line px-4 py-2.5 text-sm text-ink-2 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /> {pick("हटाएँ", "Delete")}</button>}
      </div>
      {ch && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="birth-chart">
          <Stat label={pick("लग्न", "Lagna")} v={pick(ch.lagna.deva, ch.lagna.en)} />
          <Stat label={pick("चन्द्र राशि", "Moon Rashi")} v={pick(ch.moon_rashi.deva, ch.moon_rashi.en)} />
          <Stat label={pick("जन्म नक्षत्र", "Janma Nakshatra")} v={`${ch.nakshatra.en} · ${pick("पाद", "pada")} ${ch.nakshatra.pada}`} />
          <div className="sm:col-span-3 border border-line bg-parchment p-4">
            <p className="overline-label text-ink-3">{pick("वर्तमान दशा", "Running Dasha")}</p>
            <p className="mt-1 text-sm text-ink">
              {pick("महादशा", "Maha")}: <b>{ch.vimshottari.current?.maha}</b> · {pick("अंतर्दशा", "Antar")}: <b>{ch.vimshottari.current?.antar}</b> · {pick("प्रत्यंतर", "Pratyantar")}: <b>{ch.vimshottari.current?.pratyantar}</b>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }) {
  return (<div className="border border-line bg-parchment p-4"><p className="overline-label text-ink-3">{label}</p><p className="mt-1 font-serif-latin text-lg text-ink">{v}</p></div>);
}

function ComingSoonCard({ pick }) {
  const items = [
    ["Curated temple chanting audio", "क्यूरेटेड मंदिर जप ध्वनि"],
    ["Telugu language support", "तेलुगु भाषा समर्थन"],
    ["Downloadable PDF Panchangam", "डाउनलोड योग्य PDF पंचांगम्"],
    ["Muhurta reminders on WhatsApp", "व्हाट्सऐप पर मुहूर्त स्मरण"],
  ];
  return (
    <div className="mt-8 rounded-2xl border border-line bg-surface p-6" data-testid="premium-coming-soon">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-saffron" />
        <h2 className="font-serif-latin text-xl text-ink">{pick("शीघ्र आ रहा है", "Coming soon")}</h2>
      </div>
      <p className="mt-1.5 text-sm text-ink-3">{pick("हम प्रीमियम में ये सुविधाएँ शीघ्र जोड़ रहे हैं।", "We're actively adding these to Panchangam Premium.")}</p>
      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 border border-line bg-parchment px-3 py-2.5 text-sm text-ink-2" data-testid={`coming-soon-${i}`}>
            <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-saffron/70" />
            {pick(it[1], it[0])}
          </div>
        ))}
      </div>
    </div>
  );
}

function PremiumAboutWhy({ pick }) {
  const why = [
    ["Timed to your exact place", "Every muhurta, hora and kala is computed from your precise latitude, longitude and time zone using local sunrise & sunset — not a generic city list.", "आपके सटीक स्थान हेतु", "प्रत्येक मुहूर्त, होरा एवं काल आपके अक्षांश, देशांतर एवं समय-क्षेत्र से स्थानीय सूर्योदय/सूर्यास्त पर आधारित।"],
    ["Personal, not generic", "Readings are tuned to your Lagna, Moon sign and running Dasha — so a 'good hora' is good for you.", "वैयक्तिक, सामान्य नहीं", "आपकी लग्न, चन्द्र-राशि एवं दशा के अनुसार — ताकि शुभ होरा आपके लिए शुभ हो।"],
    ["Rooted in the shastras", "Rules follow classical reckoning (Vimshottari, Tarabala, Chandrashtama) — transparent, with a source note on each result.", "शास्त्र-सम्मत", "विम्शोत्तरी, ताराबल, चन्द्राष्टम आधारित — प्रत्येक परिणाम पर स्रोत सहित।"],
    ["Private by design", "Your birth details are DPDP-compliant: consent-based, never shared, deletable anytime.", "गोपनीयता सर्वोपरि", "आपका जन्म-विवरण DPDP अनुरूप — सहमति-आधारित, साझा नहीं, कभी भी हटाने योग्य।"],
  ];
  return (
    <div className="mt-12 space-y-10">
      {/* Powered by engine */}
      <div className="border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-6" data-testid="jyotish-engine">
        <div className="flex items-center gap-2"><Star className="h-5 w-5 text-saffron" /><h2 className="font-serif-latin text-xl text-ink">{pick("संचालित — धर्मार्थ ज्योतिष इंजन", "Powered by the Dharmartha Jyotish Engine")}</h2></div>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="overline-label text-gold">{pick("यह क्या है", "What it is")}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink-2">{pick(
              "धर्मार्थ ज्योतिष इंजन हमारी अपनी खगोलीय गणना प्रणाली है जो स्विस एफेमेरिस एवं लाहिरी (चित्रपक्ष) अयनांश के साथ निरयन राशिचक्र पर आधारित है।",
              "The Dharmartha Jyotish Engine is our own astronomy layer built on Swiss Ephemeris with the Lahiri (Chitrapaksha) ayanamsa and the sidereal zodiac.")}</p>
          </div>
          <div>
            <p className="overline-label text-gold">{pick("यह कैसे कार्य करता है", "How it works")}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink-2">{pick(
              "यह आपके सटीक अक्षांश/देशांतर एवं समय-क्षेत्र पर ग्रहों की निरयन स्थिति, लग्न एवं स्थानीय सूर्योदय/सूर्यास्त की गणना करता है, फिर विम्शोत्तरी दशा, ताराबल, चन्द्राष्टम एवं होरा-स्वामी मित्रता जैसे शास्त्रीय नियम लागू करता है।",
              "It computes sidereal planetary positions, your ascendant and local sunrise/sunset for your exact coordinates & time zone, then applies classical rules — Vimshottari Dasha, Tarabala, Chandrashtama and hora-lord friendships — to personalise every timing.")}</p>
          </div>
        </div>
      </div>

      {/* Why */}
      <div data-testid="premium-why">
        <h2 className="font-serif-latin text-2xl text-ink">{pick("पंचांग प्रीमियम क्यों?", "Why Panchangam Premium?")}</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {why.map((w, i) => (
            <div key={i} className="border border-line bg-surface p-5">
              <p className="flex items-center gap-2 font-serif-latin text-lg text-ink"><Check className="h-4 w-4 text-saffron" /> {pick(w[2], w[0])}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{pick(w[3], w[1])}</p>
            </div>
          ))}
        </div>
      </div>

      {/* About */}
      <div className="border-t border-line pt-8" data-testid="premium-about">
        <h2 className="font-serif-latin text-2xl text-ink">{pick("धर्मार्थ पंचांग प्रीमियम के बारे में", "About Dharmartha Panchangam Premium")}</h2>
        <p className="mt-3 max-w-3xl text-ink-2 leading-relaxed">{pick(
          "धर्मार्थ पंचांग प्रीमियम पारंपरिक वैदिक काल-गणना को आधुनिक खगोलीय परिशुद्धता से जोड़ता है। हमारा लक्ष्य है कि प्रत्येक साधक को उसके अपने स्थान एवं जन्म के अनुसार प्रामाणिक, समझने-योग्य एवं निजी मुहूर्त एवं ज्योतिष सुलभ हो — बिना किसी भ्रम के, पूर्ण पारदर्शिता के साथ।",
          "Dharmartha Panchangam Premium blends traditional Vedic time-reckoning with modern astronomical precision. Our aim is to give every seeker authentic, understandable and private muhurtas and jyotish — computed for their own place and birth, with full transparency and a source note on every result.")}</p>
        <p className="mt-3 text-sm italic text-ink-3">{pick("प्रामाणिक समय हेतु किसी योग्य पुरोहित से भी परामर्श अवश्य करें।", "For the most authoritative timing, please also consult a qualified purohit.")}</p>
      </div>
    </div>
  );
}

function SavedLocations({ pick }) {
  const [locs, setLocs] = useState([]);
  const [label, setLabel] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => { premiumGetLocations().then(setLocs).catch(() => {}); }, []);
  const persist = async (next) => { setLocs(next); try { await premiumSaveLocations(next); } catch (e) { toast.error("Could not save"); } };
  const add = () => {
    if (!sel || !label.trim()) { toast.error(pick("लेबल एवं स्थान चुनें", "Enter a label and pick a place")); return; }
    if (locs.length >= 5) { toast.error(pick("अधिकतम 5 स्थान", "Up to 5 locations")); return; }
    persist([...locs, { label: label.trim(), ...sel }]); setLabel(""); setSel(null);
  };
  const remove = (i) => persist(locs.filter((_, idx) => idx !== i));

  return (
    <div className="mt-8 border border-line bg-surface p-6" data-testid="saved-locations">
      <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><Clock className="h-5 w-5 text-saffron" /> {pick("सहेजे गए स्थान", "Saved locations")} <span className="text-xs text-ink-3">({locs.length}/5)</span></h2>
      <div className="mt-4 space-y-2">
        {locs.map((l, i) => (
          <div key={i} className="flex items-center justify-between border border-line bg-parchment px-3 py-2 text-sm" data-testid={`saved-loc-${i}`}>
            <span><b className="text-ink">{l.label}</b> · <span className="text-ink-2">{l.name}</span></span>
            <button onClick={() => remove(i)} data-testid={`saved-loc-remove-${i}`} className="text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {locs.length < 5 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <input className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" placeholder={pick("लेबल (घर)", "Label (Home)")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="saved-loc-label" />
          <PremiumPlaceInput value={sel?.name || ""} testid="saved-loc-place" onSelect={setSel} placeholder={pick("स्थान खोजें", "Search place")} />
          <button onClick={add} data-testid="saved-loc-add" className="inline-flex items-center justify-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]"><Plus className="h-4 w-4" /> {pick("जोड़ें", "Add")}</button>
        </div>
      )}
    </div>
  );
}
